import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ConnectedAccountDTO, type IntegrationProviderDTO, type IntegrationStatus } from '../../lib/api'
import { qk } from '../../lib/query'
import { useToast } from '../ui/Toaster'

/**
 * Configurações → Integrações.
 *
 * Credentials are entered here instead of in the deploy, so connecting something no longer
 * means a platform variable and a redeploy.
 *
 * Secret fields are write-only by design: the API never sends a secret back, so the input
 * starts empty and an untouched field is left alone rather than overwritten with a blank. What
 * you get instead is whether a value is set, where it currently comes from, and its last four
 * characters — enough to tell two keys apart, useless in a screenshot.
 */

function SourceBadge({ source, unreadable }: { source: 'database' | 'environment' | null; unreadable?: boolean }) {
  // A rotated or lost SETTINGS_KEY leaves rows that cannot be decrypted. Saying so beats
  // "configurado" for a value nothing can actually use.
  if (unreadable) return <span className="cfg-badge">ilegível — salve de novo</span>
  if (!source) return <span className="cfg-badge">não configurado</span>
  // Worth distinguishing: an env-sourced value cannot be edited here, and someone wondering why
  // their change "did not stick" is usually looking at one.
  return <span className="cfg-badge">{source === 'database' ? 'salvo aqui' : 'vindo do ambiente'}</span>
}

/**
 * Marketplaces: authorise a shop, don't paste keys.
 *
 * The connected list is shown above the button because with several shops per marketplace the
 * real question is "which ones are already in?" — and re-authorising an existing shop refreshes
 * it rather than adding a duplicate, which is only obvious if you can see what exists.
 */
const ACCOUNT_STATUS: Record<string, string> = {
  connected: 'conectado',
  disconnected: 'desconectado',
  simulation: 'simulação',
  error: 'com erro',
}

function OauthSection({
  provider,
  accounts,
  stores,
}: {
  provider: IntegrationProviderDTO
  accounts: ConnectedAccountDTO[]
  stores: { id: string; name: string }[]
}) {
  const toast = useToast()
  const [storeId, setStoreId] = useState(stores[0]?.id ?? '')

  const connect = useMutation({
    mutationFn: () => api.startOauth(provider.oauth!.startPath, storeId),
    onSuccess: (r) => {
      // Same tab: the marketplace shows a login and a consent screen, and popups for that get
      // blocked or lost behind the window often enough to be worse than leaving the app.
      window.location.href = r.url
    },
    onError: (e: Error) => toast(e.message, true),
  })

  const mine = accounts.filter((a) => a.channelType === provider.oauth!.channelType)

  return (
    <>
      {mine.map((a) => (
        <div className="cfg-row" key={a.id}>
          <span
            className="stat-dot"
            style={{ background: a.connectionStatus === 'connected' ? '#12a150' : '#e5484d' }}
          />
          <div className="cfg-main">
            <div className="cfg-title">
              {a.displayName}
              <span className="cfg-badge">{ACCOUNT_STATUS[a.connectionStatus] ?? a.connectionStatus}</span>
            </div>
            <div className="cfg-sub">
              {a.externalAccountId ? `id ${a.externalAccountId}` : 'sem id'}
              {` · ${stores.find((s) => s.id === a.storeId)?.name ?? 'sem loja'}`}
            </div>
          </div>
        </div>
      ))}

      {mine.length === 0 && (
        <div className="empty" style={{ padding: 16 }}>
          Nenhuma conta conectada ainda.
        </div>
      )}

      <div className="cfg-new" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {provider.oauth!.needsStore && (
          <select className="tk-status" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <button className="btn pri sm" disabled={!storeId || connect.isPending} onClick={() => connect.mutate()}>
          {connect.isPending ? 'Abrindo…' : mine.length ? 'Conectar outra conta' : 'Conectar'}
        </button>
        <span className="cfg-note">
          Você será levado ao {provider.label} para autorizar. Reconectar uma conta já existente
          renova o acesso sem duplicar o canal.
        </span>
      </div>
    </>
  )
}

function ProviderCard({
  provider,
  status,
  canSaveSecrets,
  accounts,
  stores,
}: {
  provider: IntegrationProviderDTO
  status: IntegrationStatus | undefined
  canSaveSecrets: boolean
  accounts: ConnectedAccountDTO[]
  stores: { id: string; name: string }[]
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  const fieldStatus = (key: string) => status?.fields.find((f) => f.key === key)

  const save = useMutation({
    mutationFn: () => api.saveIntegration(provider.id, draft),
    onSuccess: () => {
      setDraft({})
      setResult(null)
      toast(`${provider.label} salvo`)
      void qc.invalidateQueries({ queryKey: qk.integrations() })
    },
    onError: (e: Error) => toast(e.message, true),
  })

  const test = useMutation({
    mutationFn: () => api.testIntegration(provider.id),
    onSuccess: (r) => setResult(r),
    onError: (e: Error) => setResult({ ok: false, message: e.message }),
  })

  const dirty = Object.keys(draft).length > 0
  const blocked = !canSaveSecrets && provider.fields.some((f) => f.secret)

  return (
    <div className="cfg-block">
      <div className="blk-h">{provider.label}</div>
      <p className="cfg-note" style={{ padding: '0 14px 10px', margin: 0 }}>
        {provider.description}
      </p>

      {provider.oauth && <OauthSection provider={provider} accounts={accounts} stores={stores} />}

      {provider.fields.map((f) => {
        const st = fieldStatus(f.key)
        return (
          <div className="cfg-row" key={f.key}>
            <div className="cfg-main">
              <div className="cfg-title">
                {f.label} <SourceBadge source={st?.source ?? null} unreadable={st?.unreadable} />
              </div>
              <div className="cfg-sub">
                {f.secret
                  ? st?.unreadable
                    ? 'Não foi possível decifrar com a chave atual do servidor'
                    : st?.configured
                      ? `Configurado ${st.hint ?? ''} · deixe em branco para manter`
                      : 'Nenhum valor salvo'
                  : (st?.value ?? '—')}
                {f.hint ? ` · ${f.hint}` : ''}
              </div>
            </div>
            <div className="cfg-actions">
              <input
                type={f.secret ? 'password' : 'text'}
                // A saved secret is never sent back, so this stays empty on purpose. A password
                // manager filling it would silently overwrite a working credential.
                autoComplete="off"
                placeholder={f.placeholder ?? (f.secret && st?.configured ? '••••••••' : '')}
                value={draft[f.key] ?? (f.secret ? '' : (st?.value ?? ''))}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                style={{ minWidth: 260 }}
              />
            </div>
          </div>
        )
      })}

      {(!provider.oauth || provider.fields.length > 0) && (
      <div className="cfg-new" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button className="btn pri sm" disabled={!dirty || blocked || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </button>
        {dirty && (
          <button className="btn ghost sm" onClick={() => setDraft({})}>
            Cancelar
          </button>
        )}
        {provider.testable && (
          <button className="btn sec sm" disabled={test.isPending} onClick={() => test.mutate()}>
            {test.isPending ? 'Testando…' : 'Testar conexão'}
          </button>
        )}
        {blocked && (
          <span className="cfg-note">SETTINGS_KEY não configurada no servidor — não é possível salvar segredos.</span>
        )}
        {result && (
          <span className="cfg-note" style={{ color: result.ok ? '#12a150' : '#e5484d' }}>
            {result.message}
          </span>
        )}
      </div>
      )}
    </div>
  )
}

export function IntegrationsTab() {
  const { data, isLoading, error } = useQuery({ queryKey: qk.integrations(), queryFn: api.integrations })

  if (isLoading) return <div className="empty">Carregando…</div>
  if (error) {
    return (
      <div className="empty" style={{ padding: 26 }}>
        Apenas administradores podem alterar integrações.
      </div>
    )
  }

  return (
    <>
      <div className="crm-head">
        <h2>Integrações</h2>
        <p>
          Credenciais ficam salvas aqui, criptografadas, em vez de no ambiente do servidor. Um
          valor deixado em branco volta a usar o que está configurado no ambiente. Nenhum segredo
          é devolvido para esta tela depois de salvo — apenas os últimos caracteres, para você
          conferir qual chave está no ar.
        </p>
      </div>

      {data?.providers.map((p) => (
        <ProviderCard
          key={p.id}
          provider={p}
          status={data.status.find((s) => s.provider === p.id)}
          canSaveSecrets={data.secretsAvailable}
          accounts={data.accounts}
          stores={data.stores}
        />
      ))}
    </>
  )
}
